import { useState } from 'react'
import type { Tel } from '@w/tel'
import { useMutation, useQuery } from '@w/react-query'
import { Button } from '@w/ui/components/button'
import { safe } from '@w/utils'
import { isCellUpdate, isRowDelete } from '../databases/database-sql'
import { reapplyDeadLetter } from '../databases/database-renames'
import { useRouterContext } from '../router/flashcards-fe.router-bindings'
import { failureReason, failureScope, nativeFailures, subscribeFailures, type FailureSummary } from './entries'
import type { SpaceResource } from '../router/spaces-resources'

type SyncProblem = Awaited<ReturnType<SpaceResource['deadLetters']['list']>>[number]

/** Re-read details under current access instead of retaining list snapshots. */
export function FailurePanel({ resource, tel, entries, letters }: { resource: SpaceResource; tel: Tel; entries: FailureSummary[]; letters: SyncProblem[] }) {
  return <section aria-label="Workspace failures" className="flex flex-col gap-3">
    <h3>Workspace failures</h3>
    {entries.map(entry => <FailureDetail key={entry.id.join(',')} resource={resource} tel={tel} entry={entry} />)}
    {letters.map(letter => <LetterDetail key={letter.id} resource={resource} tel={tel} letter={letter} />)}
  </section>
}

/** Custom dead letters (refused custom-database writes, e.g. add row or add property) that no page failure
 * accounts for. A listed page failure's Retry replays its row's cell saves (reapplyRowValues), and a row delete's
 * letter shares its page failure's frame (publication_sequence = _turso_dead_letters.sequence, reapplyRowDelete)
 * in any status, so a discarded row-delete failure leaves its letter to Sync problems only. Neither is counted twice. */
export async function unmatchedLetters(resource: SpaceResource, entries: readonly FailureSummary[]): Promise<SyncProblem[]> {
  const problems = await resource.deadLetters.list()
  if (problems.length === 0) return []
  const driver = safe(resource.db.driver)
  const rows = entries.flatMap(entry => entry.category === 'outbound' && entry.source && 'Page' in entry.source ? [entry.source.Page.id] : [])
  const letters = await driver.query<{ id: string; program: string; framed: number }>('SELECT CAST(l.local_txn_id AS TEXT) AS id,l.program,EXISTS(SELECT 1 FROM _turso_dead_letters f WHERE f.sequence=l.publication_sequence) AS framed FROM _turso_replication_dead_letters l')
  const matched = new Set(letters.filter(letter => {
    const program = JSON.parse(letter.program) as { sql: string; params?: unknown[] }[]
    if (program.length === 0) return false
    const deletes = program.every(statement => statement.params?.length === 1 && isRowDelete(statement, String(statement.params[0])))
    return (deletes && Boolean(letter.framed)) || rows.some(rowId => program.every(statement => isCellUpdate(statement, rowId)))
  }).map(letter => letter.id))
  return problems.filter(problem => !matched.has(problem.id))
}

// Reasons custom dead letters carry: the server's refusals (workspace-replication.ts denied/refused), the engine's
// E_PRIOR_LOCAL_REFUSAL, joined with "; " per refused statement. Mapped to entries.ts fixed diagnostics; anything
// else (e.g. a failed re-apply's exception) is the generic text.
const letterCodes: Record<string, string> = {
  E_PRIOR_LOCAL_REFUSAL: 'TRANSACTION_REFUSED',
}
function letterReasons(reason: string): string[] {
  return [...new Set(reason.split(';').map(part => failureReason(letterCodes[part.trim()] ?? part.trim())))]
}

/** Same calls as Sync problems (sync/sync-problems.tsx): Re-apply runs the letter again on the current database,
 * then syncs; a re-apply that fails keeps the letter listed with its new reason. */
function LetterDetail({ resource, tel, letter }: { resource: SpaceResource; tel: Tel; letter: SyncProblem }) {
  const ctx = useRouterContext()
  const [pending, setPending] = useState(false)
  const action = useMutation({ mutationFn: async (command: 'reapply' | 'discard') => {
    if (command === 'discard') { await resource.deadLetters.discard(letter.id); return }
    await reapplyDeadLetter(resource, letter)
    await ctx.spaces.trigger(resource.space.workspaceId, true, tel)
  }, onSettled: () => { setPending(false) } }, resource.queryClient)
  if (pending) return null
  return <article className="flex flex-col gap-2">
    <p>Refused change</p>
    {letterReasons(letter.reason).map(reason => <p key={reason}>{reason}</p>)}
    <div className="flex flex-wrap gap-2">
      <Button disabled={action.isPending} data-testid={`failure-letter-reapply-${letter.id}`} onClick={() => { setPending(true); action.mutate('reapply') }}>Re-apply</Button>
      <Button disabled={action.isPending} data-testid={`failure-letter-discard-${letter.id}`} onClick={() => { setPending(true); action.mutate('discard') }}>Discard</Button>
    </div>
    {action.error ? <p role="alert">Recovery unavailable. Try again.</p> : null}
  </article>
}

/** Re-applies, oldest first, the custom dead letters made only of this row page's cell saves (database-sql.ts
 * updateCell); any other refused statement (e.g. a row delete) stays for Sync problems. False when a re-apply
 * failed: that letter stays listed with its new reason. */
async function reapplyRowValues(resource: SpaceResource, rowId: string): Promise<boolean> {
  if ((await resource.deadLetters.list()).length === 0) return true
  const letters = await safe(resource.db.driver).query<{ id: string; program: string }>('SELECT CAST(local_txn_id AS TEXT) AS id,program FROM _turso_replication_dead_letters ORDER BY created_at,local_txn_id')
  for (const letter of letters) {
    const program = JSON.parse(letter.program) as { sql: string; params?: unknown[] }[]
    if (program.length === 0 || !program.every(statement => isCellUpdate(statement, rowId))) continue
    await resource.deadLetters.reapply(letter.id)
    if ((await resource.deadLetters.list()).some(problem => problem.id === letter.id)) return false
  }
  return true
}

/** A row delete is one frame (db-custom driver.ts deleteRow trash): the DELETE, retained as a custom dead letter
 * whose publication_sequence is the native page failure's outbox sequence. Selects only that failure's
 * row-delete letters, oldest first; empty when the failure is not a row delete. */
async function rowDeleteLetters(resource: SpaceResource, failureId: number[], rowId: string): Promise<{ id: string }[]> {
  // No custom letters: the letter table may not exist yet.
  if ((await resource.deadLetters.list()).length === 0) return []
  const driver = safe(resource.db.driver)
  const [failure] = await driver.query<{ sequence: number | bigint | null }>('SELECT sequence FROM _turso_dead_letters WHERE id=?', [Uint8Array.from(failureId)])
  if (failure?.sequence === null || failure?.sequence === undefined) return []
  return (await driver.query<{ id: string; program: string }>('SELECT CAST(local_txn_id AS TEXT) AS id,program FROM _turso_replication_dead_letters WHERE publication_sequence=? ORDER BY created_at,local_txn_id', [failure.sequence]))
    .filter(letter => { const program = JSON.parse(letter.program) as { sql: string; params?: unknown[] }[]; return program.length > 0 && program.every(statement => isRowDelete(statement, rowId)) })
}

/** Re-applies only the failure's row-delete letters; null when the failure is not a row delete (no such letter),
 * false when a re-apply failed. */
async function reapplyRowDelete(resource: SpaceResource, failureId: number[], rowId: string): Promise<boolean | null> {
  const letters = await rowDeleteLetters(resource, failureId, rowId)
  if (letters.length === 0) return null
  for (const letter of letters) {
    await resource.deadLetters.reapply(letter.id)
    if ((await resource.deadLetters.list()).some(problem => problem.id === letter.id)) return false
  }
  return true
}

function FailureDetail({ resource, tel, entry }: { resource: SpaceResource; tel: Tel; entry: FailureSummary }) {
  const ctx = useRouterContext()
  const [discarding, setDiscarding] = useState(false)
  const action = useMutation({ mutationFn: async (command: 'retry' | 'discard' | 'check') => {
    const failures = nativeFailures(resource), scope = await failureScope(resource)
    // Re-read under live authorization before acting on an opened panel.
    const current = await failures.detail(scope, Uint8Array.from(entry.id), Uint8Array.from(entry.digest))
    if (!current || current.status !== 'failed') return
    if (command === 'discard') {
      // Discarding a refused row delete drops its DELETE letter too; otherwise it would stay hidden behind the
      // dismissed failure (unmatchedLetters) and a later Sync problems Re-apply could still delete the row.
      if (current.category === 'outbound' && current.source && 'Page' in current.source)
        for (const letter of await rowDeleteLetters(resource, current.id, current.source.Page.id)) await resource.deadLetters.discard(letter.id)
      await failures.discard(scope, Uint8Array.from(current.id))
    }
    // Checking an inbound refusal needs the server's current authorization result.
    else if (command === 'check') await ctx.spaces.trigger(resource.space.workspaceId, true, tel)
    else {
      // A refused property save is two frames (database-grid.tsx saveCell): the cell value, retained as a
      // custom dead letter, then the row page write, retained natively. Replay them in that order; a value that
      // fails to re-apply keeps the page failure listed instead of syncing the page write without it.
      // A refused row delete is one frame instead: replay only its DELETE, never the row's cell saves.
      if (current.category === 'outbound' && current.source && 'Page' in current.source) {
        const rowId = current.source.Page.id
        const reapplied = await reapplyRowDelete(resource, current.id, rowId) ?? await reapplyRowValues(resource, rowId)
        if (!reapplied) return
      }
      // The engine decides whether the failed frame is still eligible for retry.
      const accepted = await failures.retry(scope, Uint8Array.from(current.id), Uint8Array.from(current.digest))
      if (accepted && current.category === 'outbound') await ctx.spaces.trigger(resource.space.workspaceId, true, tel)
    }
  }, onSettled: () => { setDiscarding(false) } }, resource.queryClient)
  const detail = useQuery({
    queryKey: ['workspace-failure-detail', resource.connectionId, resource.userId, entry.id, entry.digest, entry.status, entry.reasons],
    queryFn: async () => nativeFailures(resource).detail(await failureScope(resource), Uint8Array.from(entry.id), Uint8Array.from(entry.digest)),
    subscribe: (invalidate: () => void) => subscribeFailures(resource, invalidate, tel),
  }, resource.queryClient)
  if (discarding) return null
  if (!detail.data || detail.error || detail.isFetching) return <p>Failure details unavailable</p>
  return <article className="flex flex-col gap-2">
    <p>{detail.data.category} · {detail.data.status}</p>
    <p>Failure {detail.data.id.map(byte => byte.toString(16).padStart(2, '0')).join('')}</p>
    {detail.data.source ? <p>{'Page' in detail.data.source ? `Page ${detail.data.source.Page.id}` : `Card ${detail.data.source.Card.qid}`}</p> : null}
    {[...new Set(detail.data.reasons)].map(reason => <p key={reason}>{failureReason(reason)}</p>)}
    {detail.data.status === 'failed' || detail.data.status === 'pending' ? <div className="flex flex-wrap gap-2">
      <Button disabled={action.isPending || detail.data.status === 'pending'} data-failure-id={detail.data.id.map(byte => byte.toString(16).padStart(2, '0')).join('')} onClick={() => { action.mutate(detail.data?.category === 'inbound' ? 'check' : 'retry') }}>{detail.data.category === 'inbound' ? 'Check again' : 'Retry'}</Button>
      <Button disabled={action.isPending || detail.data.status === 'pending'} onClick={() => { setDiscarding(true); action.mutate('discard') }}>Discard</Button>
    </div> : null}
    {action.error ? <p role="alert">Recovery unavailable. Try again.</p> : null}
  </article>
}
